import MediaQuery from 'react-responsive';
import styled from 'styled-components';
import Grid from '#/components/UnifiedRfqs/components/Grid';
import HeadedModal from '@paradigm/design-system/src/components/HeadedModal';

import useFetchedDrfq from '@paradigm/features/src/unified-rfqs/contexts/use-fetched-drfq';
import { hourFormat } from '@paradigm/utils/src/date';
import { isTaker } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';
import {
  useTakerExecutedRfqInfo,
  useTakerOrderbookFetcher,
} from '@paradigm/features/src/unified-rfqs/ui/drfq/hooks';
import {
  TradeTapeItem,
  isFilledTrade,
} from '@paradigm/features/src/unified-rfqs/entities/drfq/trades';
import {
  ExecutedTime,
  IdsContainer,
  RfqDateInfo,
} from '#/components/UnifiedRfqs/components/DetailsModal';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { ProductInfo } from '#/components/UnifiedRfqs/components/RfqStrategySummary';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { MobileRfqInfo } from '#/components/UnifiedRfqs//components/DetailsModal';
import { processLeg } from '@paradigm/features/src/unified-rfqs/ui/drfq/legs-table-functions';
import {
  primaryProductCode,
  strategyProductCodes,
} from '@paradigm/features/src/unified-rfqs/utils/legs';
import DrfqTradeDescription from '#/components/UnifiedRfqs/drfq/TradeDescription';
import TradeLegPricesTable from '#/components/UnifiedRfqs/drfq/TradeLegPricesTable';

import ClosedDrfqLegsTable from '#/components/UnifiedRfqs/drfq/ClosedDrfqLegsTable';
import OrderbookTradeRecap from '#/components/UnifiedRfqs/drfq/trade-recap/OrderbookTradeRecap';

interface Props {
  trade: TradeTapeItem;
  isOpen: boolean;
  close: () => void;
}

export default function DrfqTradeDetailsModal({ trade, isOpen, close }: Props) {
  const { drfq } = useFetchedDrfq(trade.rfq_id);
  const { isLoading, bids, asks } = useTakerOrderbookFetcher(trade.rfq_id, {
    // If drfq is not null, it means it belongs to the viewing user
    shouldFetch: drfq != null,
  });
  const { executedFromDesk, executedOrder } = useTakerExecutedRfqInfo(
    trade.rfq_id,
    trade.side,
  );
  const isMobile = useIsMobile();
  const productCode = primaryProductCode(trade.legs);
  // trade.legs may include a hedge leg on a different product, which must
  // not count towards "is this multi-product".
  const productCodes = strategyProductCodes(trade.legs);
  const leftInfo = isMobile ? (
    <RfqDateInfo date={trade.executed_at} />
  ) : (
    <ProductInfo
      venue={trade.venue}
      productCode={productCode}
      productCodes={productCodes}
    />
  );
  const shouldDisplayTradeRecap =
    drfq != null && isTaker(drfq) && isFilledTrade(trade) && !isLoading;

  return (
    <HeadedModal
      title="Trade Recap"
      isOpen={isOpen}
      close={close}
      leftInfo={leftInfo}
      aria-label="drfq trade details modal"
    >
      <MediaQuery maxWidth={MOBILE_BREAKPOINT}>
        <MobileRfqInfo
          venue={trade.venue}
          productCode={productCode}
          description={trade.description}
        />
      </MediaQuery>
      <Grid cols={1} css="padding: 1rem;">
        <DrfqTradeDescription
          trade={trade}
          drfq={drfq}
          // Trade tape item shows info from the TAKER perspective
          viewerRole="TAKER"
          counterpartyDesk={executedFromDesk}
        />

        <LegsContainer>
          <ClosedDrfqLegsTable legs={trade.legs.map(processLeg)} />
          {!isMobile && <TradeLegPricesTable trade={trade} />}
        </LegsContainer>
        {shouldDisplayTradeRecap && (
          <OrderbookTradeRecap
            executedOrder={executedOrder}
            executionSide={trade.side}
            drfq={drfq}
            bids={bids}
            asks={asks}
          />
        )}
        <IdsContainer>
          <div>RFQ ID: {trade.rfq_id}</div>
          <div>Trade ID: {trade.id}</div>
          {executedOrder != null && <div>Order ID: {executedOrder.id}</div>}
        </IdsContainer>
        <ExecutedLabel trade={trade} />
      </Grid>
    </HeadedModal>
  );
}

function ExecutedLabel({ trade }: { trade: TradeTapeItem }) {
  switch (trade.state) {
    case 'FILLED':
      return (
        <ExecutedTime>
          <span css="opacity: 0.5">Executed at</span>{' '}
          {hourFormat(trade.executed_at)}
        </ExecutedTime>
      );

    case 'REJECTED':
      return (
        <ExecutedTime>
          <span css="opacity: 0.5">Rejected at</span>{' '}
          {hourFormat(trade.executed_at)}
        </ExecutedTime>
      );
    case 'PENDING_SETTLEMENT':
    default:
      return null;
  }
}

const LegsContainer = styled.div`
  display: flex;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    gap: 1rem;
  }
`;
